import useBreakpoint from '~/hooks/useBreakpoint';
import { useLayoutEffect, useState, type RefObject, useMemo } from 'react';
import { useTheme } from '@emotion/react';
import useLoadPriority, { LoadPriority } from '~/hooks/useLoadPriority';
import { MicrositeBreakpoint } from '~/model';

export interface BreakpointImageProps {
	ref: RefObject<HTMLImageElement | null>;
	priority: LoadPriority;
	url: (width: number, height: number, dpi: number, viewport: number) => string;
}

// transparent pixel used as placeholder until the actual image is loaded, to prevent layout shift and allow us to calculate the correct srcset based on the parent container size
const PLACEHOLDER_GIF = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

export default function useBreakpointImage(props: BreakpointImageProps) {
	const breakpoint = useBreakpoint();
	const theme = useTheme();
	const [state, setParent] = useState({ width: 0, height: 0, breakpoint });
	const render = useLoadPriority(props.priority);

	useLayoutEffect(() => {
		const parent = props.ref.current?.parentElement;

		if (!parent) {
			return;
		}

		// calculate optimized image size based on parent dimensions, but only if we haven't already loaded a larger size
		const rect = parent.getBoundingClientRect();

		if (rect.width < state.width || rect.height < state.height) {
			// never decrease the size/quality of an image
			return;
		}

		setParent({ width: Math.ceil(rect.width), height: Math.ceil(rect.height), breakpoint });
	}, [breakpoint]);

	const srcSet = useMemo(() => {
		if (!render) {
			return undefined;
		}

		const bp = breakpoint ?? MicrositeBreakpoint.SMALL;
		const width = theme.viewport[bp];
		const url = props.url(state.width, state.height, 1, width);
		const url2x = props.url(state.width, state.height, 2, width);
		const url3x = props.url(state.width, state.height, 3, width);

		if (url3x === url2x) {
			// image doesn't support 3x DPI, so we only return 1x and 2x
			return `${url} 1x, ${url2x} 2x`;
		}

		return `${url} 1x, ${url2x} 2x, ${url3x} 3x`;
	}, [render, state]);

	return { srcSet, placeholder: PLACEHOLDER_GIF };
}
